import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FetchData, { GetListAsync, GetOne } from "../../providers/dataProvider";
import { formatDate } from "../../utils/Validate";
import { SearchComponent } from "../../components/common/SearchComponent";
import { NotFound } from "../../components/common/NotFound";
import SkeletonQuotationListing from "../../components/common/skeletonQuotationListing";
import Avatar from "../../components/common/Avatar";
import "./FactoryOrder.scss";

const getFactoryReleaseId = (item) =>
  item?.aggregateId ||
  item?.factoryReleaseId ||
  item?.factoryOrderId ||
  item?.id;

const asArray = (value) => {
  if (Array.isArray(value?.payload)) return value.payload;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value)) return value;
  return [];
};

const FactoryOrderApprovalList = () => {
  const navigate = useNavigate();
  const [approvalList, setApprovalList] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { data: orderDataList } = FetchData("order", {
    pageNumber: 1,
    pageSize: 100,
  });
  const { data: projectData } = FetchData("project");
  const { data: customerData } = FetchData("Customer");
  const { data: assignedEngineerList } = FetchData("userprofile");

  const resolveApprovalDetails = (item) => {
    const factoryOrder = item?.factoryOrder || {};
    const orders = asArray(orderDataList);
    const customerOrderId = factoryOrder?.customerOrderId || factoryOrder?.orderId;
    const customerOrderReference = factoryOrder?.customerOrderReference || factoryOrder?.orderReference;
    const hasCustomerOrderId = customerOrderId !== null && customerOrderId !== undefined && customerOrderId !== "";

    const relatedOrder = orders.find((order) =>
      (
        hasCustomerOrderId &&
        (
          String(order?.id) === String(customerOrderId) ||
          String(order?.customerOrderId) === String(customerOrderId) ||
          String(order?.orderId) === String(customerOrderId)
        )
      ) ||
      (
        customerOrderReference &&
        String(order?.customerOrderReference || order?.orderReference) === String(customerOrderReference)
      )
    );

    const projectDetails = asArray(projectData).find((project) =>
      String(project?.id) === String(relatedOrder?.projectId || factoryOrder?.projectId)
    );
    const customerDetails = asArray(customerData).find((customer) =>
      String(customer?.id) === String(relatedOrder?.customerId || factoryOrder?.customerId)
    );
    const assignedEngineerDetails = asArray(assignedEngineerList).find((engineer) =>
      String(engineer?.identityId || engineer?.id) === String(relatedOrder?.salesEngineerId || factoryOrder?.salesEngineerId)
    );
    const engineerName = [
      assignedEngineerDetails?.firstName,
      assignedEngineerDetails?.lastName,
    ].filter(Boolean).join(" ");

    return {
      factoryOrder,
      relatedOrder,
      projectName: projectDetails?.projectName || relatedOrder?.projectName || factoryOrder?.projectName || item?.projectName || "-",
      customerName: customerDetails?.customerName || relatedOrder?.customerName || factoryOrder?.customerName || item?.customerName || "-",
      salesEngineerName: engineerName || relatedOrder?.salesEngineerName || factoryOrder?.salesEngineerName || item?.salesEngineerName || "-",
      referenceNo: item?.referenceNo || factoryOrder?.factoryReleaseReference || "-",
    };
  };

  const fetchFactoryOrderDetails = async (approvalItem) => {
    const factoryReleaseId = getFactoryReleaseId(approvalItem);
    if (!factoryReleaseId) return approvalItem;

    const result = await new Promise((resolve) => {
      GetOne("factoryOrder", { id: factoryReleaseId }, (response) => resolve(response));
    });

    return {
      ...approvalItem,
      factoryOrder: result?.message === "success" ? result?.data : null,
    };
  };

  const fetchPendingApprovals = async () => {
    setLoading(true);
    const result = await GetListAsync("factoryApprovalPending", {});
    const rawList = Array.isArray(result?.data)
      ? result.data
      : Array.isArray(result?.data?.payload)
        ? result.data.payload
        : [];

    const enrichedList = await Promise.all(rawList.map(fetchFactoryOrderDetails));
    setApprovalList(enrichedList);
    setDisplayList(enrichedList);
    setLoading(false);
  };

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
      detail: {
        pathname: "/factory-order-approval",
        title: "Factory Order Approval",
      },
    }));

    return () => {
      window.dispatchEvent(new CustomEvent("pathfinderHeaderMetaChange", {
        detail: {
          pathname: "/factory-order-approval",
          clear: true,
        },
      }));
    };
  }, []);

  const filteredResults = useMemo(() => {
    if (!search?.trim()) return approvalList;

    const query = search.trim().toLowerCase();
    return approvalList.filter((item) => {
      const details = resolveApprovalDetails(item);
      return [
        item?.referenceNo,
        item?.requesterName,
        item?.status,
        details.referenceNo,
        details.projectName,
        details.customerName,
        details.salesEngineerName,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [approvalList, search, orderDataList, projectData, customerData, assignedEngineerList]);

  useEffect(() => {
    setDisplayList(filteredResults);
  }, [filteredResults]);

  const applySearch = () => {
    setDisplayList(filteredResults);
  };

  const removeSearch = () => {
    setSearch("");
    setDisplayList(approvalList);
  };

  const handleView = (item) => {
    navigate("/factory-order-approval-view", {
      state: {
        approval: item,
        id: getFactoryReleaseId(item),
      },
    });
  };

  return (
    <section id="factoryOrderList">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 px-0 py-1">
            <div className="card">
              <div className="card-body">
                <div className="row search-area-row">
                  <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-4 col-sm-12 col-xs-12 my-auto p-0">
                    <p>{displayList?.length || 0} Results Found</p>
                  </div>
                  <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-8 col-sm-12 col-xs-12 my-auto p-0">
                    <SearchComponent
                      filteredResults={displayList}
                      search={search}
                      handleKeyDown={(e) => e.key === "Enter" && applySearch()}
                      setSearch={setSearch}
                      removeSearch={removeSearch}
                      ApplySearch={applySearch}
                      RefreshList={fetchPendingApprovals}
                      data={approvalList}
                      HideFilter="HideFilter"
                      menuRights={{ canAdd: false }}
                      placeholder="Search approval"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonQuotationListing rows={8} />
        ) : displayList?.length > 0 ? (
          <div className="row order-table-row row-height mb-1">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
              <div className="card mt-0 order-list-card">
                <div className="card-body">
                  <div className="table-responsive order-table-responsive">
                    <table className="table table-borderless table-list order-table-list">
                      <thead>
                        <tr>
                          <th><div>PROJECT</div></th>
                          <th><div>FACTORY RELEASE REF</div></th>
                          <th><div>CUSTOMER</div></th>
                          <th><div>SALES ENGINEER</div></th>
                          <th><div>REQUESTER</div></th>
                          <th><div>REQUESTED AT</div></th>
                          <th><div>STATUS</div></th>
                          <th><div>ACTIONS</div></th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayList.map((item, index) => {
                          const details = resolveApprovalDetails(item);

                          return (
                            <tr key={item?.approvalRequestId || item?.aggregateId || index}>
                              <td>
                                <div className="project-cell">
                                  <Avatar preName={details.projectName} />
                                  <h5 title={details.projectName}>{details.projectName}</h5>
                                </div>
                              </td>
                              <td><h5 title={details.referenceNo}>{details.referenceNo}</h5></td>
                              <td><h5 title={details.customerName}>{details.customerName}</h5></td>
                              <td><h5 title={details.salesEngineerName}>{details.salesEngineerName}</h5></td>
                              <td><h5 title={item?.requesterName}>{item?.requesterName || "-"}</h5></td>
                              <td><h5>{formatDate(item?.requestedAt) || "-"}</h5></td>
                              <td><h5>{item?.status || "-"}</h5></td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm update-btn"
                                  onClick={() => handleView(item)}
                                  style={{ color: "#fff", background: "#005795", borderColor: "#005795" }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <NotFound />
        )}
      </div>
    </section>
  );
};

export default FactoryOrderApprovalList;
